import { createRouter, createWebHistory } from "vue-router";
import { isIndexableVideoPilot } from "../../../lib/video-indexing.js";
import PublicHome from "../views/PublicHome.vue";
import { authManager } from "../services/auth.js";
import tierService from "../services/tierService.js";
import { labelUntrackedHumblyticsTargets } from "../services/analytics.js";

const Home = () => import("../views/Home.vue");
const PublicVideoDetail = () => import("../views/PublicVideoDetail.vue");
const PublicVideoEdit = () => import("../views/PublicVideoEdit.vue");
const Analytics = () => import("../views/Analytics.vue");
const Topics = () => import("../views/Topics.vue");
const Concepts = () => import("../views/Concepts.vue");
const Elements = () => import("../views/Elements.vue");
const Formats = () => import("../views/Formats.vue");
const AccountTypes = () => import("../views/AccountTypes.vue");
const PublicConceptsArchive = () => import("../views/PublicConceptsArchive.vue");
const PublicFormatsArchive = () => import("../views/PublicFormatsArchive.vue");
const PublicTopicsArchive = () => import("../views/PublicTopicsArchive.vue");
const PublicElementsArchive = () => import("../views/PublicElementsArchive.vue");
const PublicAccountTypesArchive = () => import("../views/PublicAccountTypesArchive.vue");
const PublicHookStrategiesArchive = () => import("../views/PublicHookStrategiesArchive.vue");
const PublicCut30Creators = () => import("../views/PublicCut30Creators.vue");
const PublicCreators = () => import("../views/PublicCreators.vue");
const PublicCut30PrivatePosts = () => import("../views/PublicCut30PrivatePosts.vue");
const PublicReportDetail = () => import("../views/PublicReportDetail.vue");
const BotAdmin = () => import("../views/BotAdmin.vue");
const Swipefiles = () => import("../views/Swipefiles.vue");
const SwipefileDetail = () => import("../views/SwipefileDetail.vue");
const SwipefileBoard = () => import("../views/SwipefileBoard.vue");
const Views = () => import("../views/Views.vue");
const Feed = () => import("../views/Feed.vue");
const SubmitVideo = () => import("../views/SubmitVideo.vue");
const Queue = () => import("../views/Queue.vue");
const CreateTarget = () => import("../views/CreateTarget.vue");
const UserManagement = () => import("../views/admin/UserManagement.vue");
const Invites = () => import("../views/admin/Invites.vue");
const Login = () => import("../views/Login.vue");
const Signup = () => import("../views/Signup.vue");
const InviteSignup = () => import("../views/InviteSignup.vue");
const InviteConnect = () => import("../views/InviteConnect.vue");
const AuthCallback = () => import("../views/AuthCallback.vue");
const Privacy = () => import("../views/Privacy.vue");
const Terms = () => import("../views/Terms.vue");
const Updates = () => import("../views/Updates.vue");
const BlogArchive = () => import("../views/BlogArchive.vue");
const BlogArticle = () => import("../views/BlogArticle.vue");
const BlogAuthor = () => import("../views/BlogAuthor.vue");
const About = () => import("../views/About.vue");
const Subscription = () => import("../views/Subscription.vue");
const CheckoutSuccess = () => import("../views/CheckoutSuccess.vue");
const CheckoutCancel = () => import("../views/CheckoutCancel.vue");
const Profile = () => import("../views/Profile.vue");
const PublicMcpInstall = () => import("../views/PublicMcpInstall.vue");
const PublicToolsHub = () => import("../views/PublicToolsHub.vue");
import REELS_SIZE_TOOL from "../data/tools/instagram-reels-size.js";
import { TOOLS_HUB } from "../data/tools/index.js";
const PublicReelsSizeChecker = () => import("../views/PublicReelsSizeChecker.vue");
const OAuthConsent = () => import("../views/OAuthConsent.vue");

const routes = [
  // Public routes (no authentication required)
  {
    path: "/",
    name: "PublicHome",
    component: PublicHome,
    meta: {
      title: "VideoDatabase | Instagram and TikTok Video Reference Library",
      description: "Discover high-performing video strategies. Browse our curated database of successful short-form videos.",
      requiresAuth: false,
    },
  },
  {
    path: "/mcp/install",
    name: "PublicMcpInstall",
    component: PublicMcpInstall,
    meta: {
      title: "VideoDatabase MCP for Codex, ChatGPT, Claude, and Grok",
      description: "Connect ChatGPT, Claude, Codex or Grok to VideoDatabase to search analyzed Reels and TikToks, and on Pro, write hooks, ideas and scripts in your voice.",
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    path: "/tools",
    name: "PublicToolsHub",
    component: PublicToolsHub,
    meta: {
      title: TOOLS_HUB.title,
      description: TOOLS_HUB.description,
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    path: "/tools/instagram-reels-size",
    name: "PublicReelsSizeChecker",
    component: PublicReelsSizeChecker,
    meta: {
      title: REELS_SIZE_TOOL.title,
      description: REELS_SIZE_TOOL.description,
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    // Sign-in and Allow card for MCP OAuth. No site chrome, so nothing covers it.
    path: "/oauth/consent",
    name: "OAuthConsent",
    component: OAuthConsent,
    meta: {
      title: "Authorize AI Connection | VideoDatabase",
      description: "Review and approve an AI application's access to VideoDatabase.",
      requiresAuth: false,
      skipInterestModal: true,
      noIndex: true,
      bare: true,
    },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      title: "Sign In | Videodatabase.org",
      description: "Sign in to access Video Viewer (existing accounts only)",
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    path: "/signup",
    name: "Signup",
    component: Signup,
    meta: {
      title: "Sign Up | Videodatabase.org",
      description: "Create a new account to access Video Database",
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    path: "/signup/invite/:token",
    name: "InviteSignup",
    component: InviteSignup,
    meta: {
      title: "Setup Pro Account | Videodatabase.org",
      description: "Setup your free Pro account",
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    // Bare invite sign-in for the AI Video Editor setup flow. No site chrome.
    path: "/connect/:token",
    name: "InviteConnect",
    component: InviteConnect,
    meta: {
      title: "Connect VideoDatabase",
      description: "Connect VideoDatabase to your AI Video Editor",
      requiresAuth: false,
      skipInterestModal: true,
      noIndex: true,
      bare: true,
    },
  },
  {
    path: "/auth/callback",
    name: "AuthCallback",
    component: AuthCallback,
    meta: {
      title: "Signing In... | Videodatabase.org",
      description: "Completing authentication",
      requiresAuth: false,
      skipInterestModal: true,
    },
  },
  {
    path: "/privacy",
    name: "Privacy",
    component: Privacy,
    meta: {
      title: "VideoDatabase Privacy Policy",
      description: "Privacy Policy for VideoDatabase - Learn how we collect, use, and protect your personal information.",
      requiresAuth: false,
    },
  },
  {
    path: "/report/:id",
    name: "PublicReportDetail",
    component: PublicReportDetail,
    props: true,
    meta: {
      title: "Report - VideoDB",
      description: "View shared insight report",
      requiresAuth: false,
    },
  },
  {
    path: "/cut30-content-review",
    name: "PublicCut30ContentReviewsArchive",
    component: () => import("../views/PublicCut30ContentReviewsArchive.vue"),
    meta: {
      title: "Cut30 Content Reviews",
      description: "Browse weekly short-form video insights from the Cut30 community.",
      requiresAuth: false,
    },
  },
  {
    path: "/cut30-content-review/:id",
    name: "PublicCut30Newsletter",
    component: () => import("../views/PublicCut30Newsletter.vue"),
    props: true,
    meta: {
      title: "Cut30 Content Review",
      description: "Weekly short-form video insights from the Cut30 community",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/strategy-audit/:id",
    name: "PublicStrategyAudit",
    component: () => import("../views/PublicStrategyAudit.vue"),
    props: true,
    meta: {
      title: "Strategy Audit",
      description: "Content strategy audit and recommendations",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30-archetypes",
    name: "PublicCut30Archetypes",
    component: () => import("../views/PublicCut30Archetypes.vue"),
    meta: {
      title: "Content Archetypes | Cut30",
      description: "Content strategy archetypes for brands and creators",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/hooks",
    name: "PublicHooksHub",
    component: () => import("../views/PublicHooksHub.vue"),
    meta: {
      title: "Video Hooks: Types, Strategies, Templates & Examples",
      description: "Study what viewers see, hear, and read in the first three seconds through hook types, strategies, reusable templates, and real video examples.",
      requiresAuth: false,
    },
  },
  {
    path: "/cut30-hooks",
    name: "PublicHookTypes",
    component: () => import("../views/PublicHookTypes.vue"),
    meta: {
      title: "Video Hook Strategies",
      description: "The 7 psychological hook patterns that stop the scroll",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/terms",
    name: "Terms",
    component: Terms,
    meta: {
      title: "VideoDatabase Terms of Service",
      description: "Terms of Service for VideoDatabase - Learn about our terms, intellectual property rights, and acceptable use policies.",
      requiresAuth: false,
    },
  },
  {
    path: "/about",
    name: "About",
    component: About,
    meta: {
      title: "About VideoDatabase: Short-Form Video Reference Library",
      description: "Learn how VideoDatabase systematically studies proven short-form video patterns across formats, concepts, topics, elements, and hooks.",
      requiresAuth: false,
    },
  },
  {
    path: "/subscription",
    name: "Subscription",
    component: Subscription,
    meta: {
      title: "Subscription | Videodatabase.org",
      description: "Manage your subscription and billing",
      requiresAuth: true,
    },
  },
  {
    path: "/checkout-success",
    name: "CheckoutSuccess",
    component: CheckoutSuccess,
    meta: {
      title: "Payment Successful | Videodatabase.org",
      description: "Your payment was processed successfully",
      requiresAuth: false, // Allow access even if not authenticated (webhook handles it)
    },
  },
  {
    path: "/checkout-cancel",
    name: "CheckoutCancel",
    component: CheckoutCancel,
    meta: {
      title: "Checkout Cancelled | Videodatabase.org",
      description: "Your checkout was cancelled",
      requiresAuth: false,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: {
      title: "Profile Settings | Videodatabase.org",
      description: "Manage your profile and account settings",
      requiresAuth: true,
    },
  },
  {
    path: "/updates",
    name: "Updates",
    component: Updates,
    meta: {
      title: "Short-Form Video Trends and Updates",
      description: "Latest posts and automated updates about new videos added to the database",
      requiresAuth: false,
    },
    props: (route) => ({
      page: parseInt(route.query.page) || 1
    }),
  },
  {
    path: "/blog",
    name: "BlogArchive",
    component: BlogArchive,
    meta: {
      title: "VideoDatabase Editorial",
      description: "Original research on the creators, formats, hooks, and strategies shaping short-form video, grounded in analyzed Instagram and TikTok examples.",
      requiresAuth: false,
    },
  },
  {
    path: "/blog/author/:slug",
    name: "BlogAuthor",
    component: BlogAuthor,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: "/blog/preview/:id",
    name: "BlogPreview",
    component: BlogArticle,
    props: true,
    meta: { title: "Blog Preview", requiresAuth: true, requiresTier: ['admin'], skipInterestModal: true },
  },
  {
    path: "/blog/top-fitness-influencers",
    redirect: "/blog/best-fitness-influencers",
    meta: { requiresAuth: false },
  },
  {
    path: "/blog/:slug",
    name: "BlogArticle",
    component: BlogArticle,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: "/embed-test",
    name: "EmbedTest",
    component: () => import("../views/EmbedTest.vue"),
    meta: {
      title: "Embed Component Test",
      description: "Test page for SocialVideoEmbed component",
      requiresAuth: false,
    },
  },
  {
    path: "/test-hooks-week",
    name: "TestHooksWeek",
    component: () => import("../views/TestHooksWeek.vue"),
    meta: {
      title: "What's Hooking This Week",
      description: "Test page for weekly hook analysis feature",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  { path: "/posts/:slug", redirect: to => `/blog/${to.params.slug}` },
  {
    path: "/updates/:id",
    name: "UpdateDetail",
    component: () => import("../views/UpdateDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
  path: "/public-beta",
  redirect: "/videos",
  },
  // Video detail routes - full slug format only
  {
    path: "/video/:slug",
    name: "PublicVideoDetail",
    component: PublicVideoDetail,
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  // Full edit timeline (shots, graphics, text) for one video; opened from the
  // video page's timeline strip. Sign-in is handled in the view.
  {
    path: "/video/:slug/edit",
    name: "PublicVideoEdit",
    component: PublicVideoEdit,
    props: true,
    meta: {
      title: "The edit",
      requiresAuth: false,
      skipInterestModal: true,
      noIndex: true,
    },
  },
  
  // Public videos page - search works, filters require auth
  {
    path: "/videos",
    name: "Home",
    component: Home,
    meta: {
      title: "Instagram and TikTok Video Database",
      description: "Browse and search all analyzed videos from Instagram and TikTok",
      requiresAuth: false,
    },
  },
  {
    path: "/home",
    redirect: "/videos",
  },
  {
    path: "/analytics",
    name: "Analytics",
    component: Analytics,
    meta: {
      title: "Video Viewer - Analytics",
      description: "View analytics and insights from video analysis",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/feed",
    name: "Feed",
    component: Feed,
    meta: {
      title: "Feed - VideoDB",
      description: "Your personalized video feed",
      requiresAuth: true,
      // Feed available to all authenticated users (basic, creator, strategist, admin)
    },
  },
  {
    path: "/submit-video",
    name: "SubmitVideo",
    component: SubmitVideo,
    meta: {
      title: "Submit Video - VideoDB",
      description: "Submit an Instagram or TikTok video for analysis",
      requiresAuth: true
    },
  },
  {
    path: "/queue",
    name: "Queue",
    component: Queue,
    meta: {
      title: "Submission Queue - VideoDB",
      description: "View the status of your submitted videos",
      requiresAuth: true
    },
  },
  {
    path: "/create-target",
    name: "CreateTarget",
    component: CreateTarget,
    meta: {
      title: "Create Target - VideoDB",
      description: "Add a TikTok or Instagram username to monitor for new videos",
      requiresAuth: true,
      requiresTier: ['strategist', 'admin'],
    },
  },
  // Public taxonomy archive pages (must come before detail pages)
  {
    path: "/concepts",
    name: "PublicConceptsArchive",
    component: PublicConceptsArchive,
    meta: {
      title: "Video Concepts: Breakdowns, Skits, Hot Takes & More",
      description: "Breakdowns, Skits, Hot Takes and 120+ more video concepts, each with real Instagram and TikTok examples to study before you script.",
      requiresAuth: false,
    },
  },
  {
    path: "/formats",
    name: "PublicFormatsArchive",
    component: PublicFormatsArchive,
    meta: {
      title: "Short-Form Video Formats: Vlogs, Skits, Talking Heads & More",
      description: "Vlogs, Skits, One Shots and six kinds of talking head: browse 20+ short-form video formats, each with real Instagram and TikTok examples.",
      requiresAuth: false,
    },
  },
  {
    path: "/topics",
    name: "PublicTopicsArchive",
    component: PublicTopicsArchive,
    meta: {
      title: "Video Topics: Creators to Study in Fitness, Food & More",
      description: "Fitness, food, real estate and 200+ more niches. Watch each one's top Instagram and TikTok videos and see which creators are worth studying.",
      requiresAuth: false,
    },
  },
  {
    path: "/elements",
    name: "PublicElementsArchive",
    component: PublicElementsArchive,
    meta: {
      title: "Video Elements: On-Screen Text, Editing, Camera & Sound",
      description: "Text overlays, jump cuts, B-roll, voiceovers and 100+ more video elements, each with real Instagram and TikTok examples to study before you shoot.",
      requiresAuth: false,
    },
  },
  {
    path: "/account-types",
    name: "PublicAccountTypesArchive",
    component: PublicAccountTypesArchive,
    meta: {
      title: "Video Account Types",
      description: "6 account type categories: Personal Brand, Brand, Curator, Media, Expert, and Local Business. See how each type creates content.",
      requiresAuth: false,
    },
  },
  {
    path: "/hook-strategies",
    name: "PublicHookStrategiesArchive",
    component: PublicHookStrategiesArchive,
    meta: {
      title: "Video Hook Strategies",
      description: "7 psychological hook patterns that stop the scroll: Identity & Specificity, Opinions & Polarization, Curiosity & Open Loops, and more.",
      requiresAuth: false,
    },
  },
  {
    path: '/hook-types/:slug(curiosity-open-loops|transformation-payoff|secrets-shortcuts|relatability-contrast|credibility|opinions-polarization|identity-specificity)',
    redirect: to => ({ path: '/hooks/' + to.params.slug })
  },
  {
    path: "/hook-types",
    name: "PublicHookTypesArchive",
    component: () => import("../views/PublicHookTypeNewArchive.vue"),
    meta: {
      title: "Video Hook Types: Text, Verbal and Visual, with Examples",
      description: "The three ways a video hook reaches the viewer: the first shot, the on-screen text and the first spoken line, with real Reels and TikTok examples of each.",
      requiresAuth: false,
    },
  },
  {
    path: "/hook-types/:slug",
    name: "PublicHookTypeDetail",
    component: () => import("../views/PublicHookTypeDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/frameworks",
    name: "PublicFrameworksArchive",
    component: () => import("../views/PublicFrameworksArchive.vue"),
    meta: {
      title: "Video Frameworks",
      description: "Browse all video frameworks - structural narrative patterns in short-form video",
      requiresAuth: false,
    },
  },
  {
    path: "/archetypes",
    name: "PublicArchetypesArchive",
    component: () => import("../views/PublicArchetypesArchive.vue"),
    meta: {
      title: "Creator Archetypes",
      description: "Explore content strategy archetypes such as The Yapper, The Entertainer, The True Expert, and The Showoff.",
      requiresAuth: false,
    },
  },
  {
    path: "/hooks/:slug",
    name: "PublicHookStrategyDetail",
    component: () => import("../views/PublicHookStrategyDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/hook-strategies/:slug',
    redirect: to => ({ path: '/hooks/' + to.params.slug })
  },
  {
    path: "/cut30",
    name: "PublicCut30Students",
    component: PublicCut30Creators,
    meta: {
      title: "Cut30 Students | Video Examples",
      description: "See the success of Cut30 students - real results from real creators",
      requiresAuth: false,
    },
  },
  {
    path: "/cut30-embed",
    name: "PublicCut30StudentsEmbed",
    component: PublicCut30Creators,
    meta: {
      title: "Cut30 Students Embed | Video Examples",
      description: "Embeddable Cut30 students creator showcase",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30embed-compact",
    name: "PublicCut30StudentsEmbedCompact",
    component: PublicCut30Creators,
    meta: {
      title: "Cut30 Students Compact Embed | Video Examples",
      description: "Compact embeddable Cut30 students creator showcase",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30-testimonials",
    name: "PublicCut30Testimonials",
    component: PublicCut30Creators,
    meta: {
      title: "Cut30 Testimonials | Video Examples",
      description: "Testimonials from Cut30 students and creators",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30-students",
    name: "PublicCut30StudentsAll",
    component: PublicCut30Creators,
    meta: {
      title: "Cut30 Students | Video Examples",
      description: "All Cut30 student creators and their niches",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30-chart-embed",
    name: "Cut30ChartEmbed",
    component: () => import("../views/Cut30ChartEmbed.vue"),
    meta: {
      title: "Cut30 Student Posts | Chart",
      description: "Bubble timeline of Cut30 student post performance",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/cut30-chart-embed-2",
    name: "Cut30ChartEmbed2",
    component: () => import("../views/Cut30ChartEmbed2.vue"),
    meta: {
      title: "Cut30 Student Posts | Raw Data",
      description: "Raw scatter plot of Cut30 student post performance",
      requiresAuth: false,
      embed: true,
    },
  },
  {
    path: "/creators",
    name: "PublicCreators",
    component: PublicCreators,
    meta: {
      title: "Instagram and TikTok Creators",
      description: "Monitored creators mapped to account types, archetypes, and the formats and strategies they actually use.",
      requiresAuth: false,
    },
  },
  {
    path: "/cut30-private",
    name: "PublicCut30PrivatePosts",
    component: PublicCut30PrivatePosts,
    meta: {
      title: "Cut30 Private Posts | Video Database",
      description: "Private posts from Cut30 student accounts",
      requiresAuth: true,
      requiresTier: ['admin'],
      noIndex: true,
    },
  },
  {
    path: "/cut30-private/top-posts",
    name: "Cut30TopPosts",
    component: () => import("../views/Cut30TopPosts.vue"),
    meta: {
      title: "Top Posts | Cut30 Private | Video Database",
      description: "Top performing posts from Cut30 student accounts",
      requiresAuth: true,
      requiresTier: ['admin'],
      noIndex: true,
    },
  },
  
  // Admin dashboard
  {
    path: "/admin",
    name: "AdminDashboard",
    component: () => import("../views/admin/Dashboard.vue"),
    meta: {
      title: "Dashboard - Admin",
      description: "Admin activity dashboard",
      requiresAuth: true,
      requiresTier: ['admin'],
      noIndex: true,
    },
  },
  {
    path: "/admin/tool-usage",
    name: "AdminToolUsage",
    component: () => import("../views/admin/ToolUsage.vue"),
    meta: {
      title: "MCP Usage - Admin",
      description: "Monitor MCP tool usage, call spacing, and connection health",
      requiresAuth: true,
      requiresTier: ['admin'],
      noIndex: true,
    },
  },
  // Admin creator management
  {
    path: "/admin/creators",
    name: "AdminCreators",
    component: () => import("../views/admin/Creators.vue"),
    meta: {
      title: "Creators - Admin",
      description: "Manage creators and target matching",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  // Admin taxonomy management pages
  {
    path: "/admin/topics",
    name: "Topics",
    component: Topics,
    meta: {
      title: "Video Viewer - Topics",
      description: "Browse topics taxonomy",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/concepts",
    name: "Concepts",
    component: Concepts,
    meta: {
      title: "Video Viewer - Concepts",
      description: "Browse concepts taxonomy",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/elements",
    name: "Elements",
    component: Elements,
    meta: {
      title: "Video Viewer - Elements",
      description: "Browse elements taxonomy",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/formats",
    name: "Formats",
    component: Formats,
    meta: {
      title: "Video Viewer - Formats",
      description: "Browse formats taxonomy",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/account-types",
    name: "AccountTypes",
    component: AccountTypes,
    meta: {
      title: "Video Viewer - Account Types",
      description: "Browse account types taxonomy",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/competitors/accounts",
    name: "CompetitorAccounts",
    component: () => import("../views/admin/CompetitorAccounts.vue"),
    meta: {
      title: "Competitor Accounts - Admin",
      description: "View competitor accounts",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/competitors/videos",
    name: "CompetitorVideos",
    component: () => import("../views/admin/CompetitorVideos.vue"),
    meta: {
      title: "Competitor Videos - Admin",
      description: "View competitor videos",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/competitors/posts",
    name: "CompetitorPosts",
    component: () => import("../views/admin/CompetitorPosts.vue"),
    meta: {
      title: "Competitor Posts - Admin",
      description: "View competitor posts",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/bot-admin",
    name: "BotAdmin",
    component: BotAdmin,
    meta: {
      title: "Video Viewer - Bot Admin",
      description: "Manage AI bot prompts and settings",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/content-generation",
    name: "ContentGeneration",
    component: () => import("../views/admin/ContentGeneration.vue"),
    meta: {
      title: "Content Generation | Admin",
      description: "Manage content generation prompts",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/blog",
    name: "BlogAdmin",
    component: () => import("../views/admin/BlogAdmin.vue"),
    meta: {
      title: "Editorial Blog | Admin",
      description: "Research, write, review, and schedule VideoDatabase editorial articles.",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/cut30-newsletter",
    name: "Cut30NewsletterAdmin",
    component: () => import("../views/admin/Cut30Newsletter.vue"),
    meta: {
      title: "Cut30 Newsletter | Admin",
      description: "Generate Cut30 weekly digests and newsletters",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/strategy-audit",
    name: "StrategyAuditAdmin",
    component: () => import("../views/admin/StrategyAudit.vue"),
    meta: {
      title: "Strategy Audit | Admin",
      description: "Generate content strategy audits for Cut30 students",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/search-settings",
    name: "SearchSettings",
    component: () => import("../views/admin/SearchSettings.vue"),
    meta: {
      title: "Search Settings | Admin",
      description: "Configure semantic search behavior",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/search-logs",
    name: "SearchLogs",
    component: () => import("../views/admin/SearchLogs.vue"),
    meta: {
      title: "Search Logs | Admin",
      description: "View user search queries",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/charts",
    name: "AdminCharts",
    component: () => import("../views/admin/Charts.vue"),
    meta: {
      title: "Charts | Admin",
      description: "View post volume charts and analytics",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/verification",
    name: "AdminVerification",
    component: () => import("../views/admin/Verification.vue"),
    meta: {
      title: "Verification | Admin",
      description: "AI-powered taxonomy verification and cleanup",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/embeds",
    name: "AdminEmbeds",
    component: () => import("../views/admin/Embeds.vue"),
    meta: {
      title: "Embeds | Admin",
      description: "Manage embeddable iframe pages",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/creator-panel",
    name: "AdminCreatorPanel",
    component: () => import("../views/admin/CreatorPanelAdmin.vue"),
    meta: {
      title: "Creator Panel | Admin",
      description: "Manage Creator Panel prompts and field configuration",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/taxonomy-examples",
    name: "AdminTaxonomyExamples",
    component: () => import("../views/admin/TaxonomyExamples.vue"),
    meta: {
      title: "Curated Content | Admin",
      description: "Curate confirmed video examples, anchors, and featured creators",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/hook-templates",
    name: "AdminHookTemplates",
    component: () => import("../views/admin/HookTemplates.vue"),
    meta: {
      title: "Hook Templates | Admin",
      description: "Browse and filter the hook template library",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/email-submissions",
    name: "AdminEmailSubmissions",
    component: () => import("../views/admin/EmailSubmissions.vue"),
    meta: {
      title: "Email Submissions | Admin",
      description: "Manage email-based video submissions and sender whitelist",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/onboarding",
    name: "CreatorOnboarding",
    component: () => import("../views/CreatorOnboarding.vue"),
    meta: {
      title: "Set Up Your Creator Profile | Videodatabase.org",
      requiresAuth: true,
    },
  },
  {
    path: "/admin/users",
    name: "UserManagement",
    component: UserManagement,
    meta: {
      title: "User Management - VideoDB",
      description: "Manage user tiers and permissions",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/admin/invites",
    name: "AdminInvites",
    component: Invites,
    meta: {
      title: "Invites - VideoDB",
      description: "Manage invite links for free Pro accounts",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/swipefiles",
    name: "Swipefiles",
    component: Swipefiles,
    meta: {
      title: "Swipefiles | VideoDatabase",
      description: "Manage your saved video collections",
      requiresAuth: true,
    },
  },
  {
    path: "/swipefiles/:id",
    name: "SwipefileDetail",
    component: SwipefileDetail,
    props: true,
    meta: {
      title: "Swipefile | VideoDatabase",
      description: "View and manage videos in your swipefile",
      requiresAuth: true,
    },
  },
  {
    path: "/board",
    name: "NewBoard",
    component: () => import("../views/NewBoard.vue"),
    meta: {
      title: "New Board | VideoDatabase",
      description: "Start a new swipe board",
      requiresAuth: true,
    },
  },
  {
    path: "/swipefiles/:id/board",
    name: "SwipefileBoard",
    component: SwipefileBoard,
    props: true,
    meta: {
      title: "Board | VideoDatabase",
      description: "Board view of your swipefile",
      requiresAuth: true,
    },
  },
  {
    path: "/views",
    name: "Views",
    component: Views,
    meta: {
      title: "Video Viewer - Views",
      description: "Manage your custom video views and collections",
      requiresAuth: true,
      requiresTier: ['admin'],
    },
  },
  {
    path: "/views/:slug",
    name: "ViewDetail",
    component: () => import("../views/ViewDetail.vue"),
    props: true,
    meta: {
      description: "View videos filtered by custom view",
      requiresAuth: false,  // Allow public access to view content
      // No requiresTier - public access
    },
  },
  
  // Public taxonomy detail pages
  {
    path: "/formats/:slug",
    name: "PublicFormatDetail",
    component: () => import("../views/PublicFormatDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/concepts/:slug",
    name: "PublicConceptDetail",
    component: () => import("../views/PublicConceptDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/topics/:slug",
    name: "PublicTopicDetail",
    component: () => import("../views/PublicTopicDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/elements/:slug",
    name: "PublicElementDetail",
    component: () => import("../views/PublicElementDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/frameworks/:slug",
    name: "PublicFrameworkDetail",
    component: () => import("../views/PublicFrameworkDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/archetypes/:slug",
    name: "PublicArchetypeDetail",
    component: () => import("../views/PublicArchetypeDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/dna/:slug",
    name: "PublicDnaDetail",
    component: () => import("../views/PublicDnaDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/creator/:slug",
    name: "PublicCreatorDetail",
    component: () => import("../views/PublicCreatorDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/account/:slug",
    name: "PublicTargetDetail",
    component: () => import("../views/PublicTargetDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
    // Note: Redirects are handled at the edge middleware level (middleware.js)
    // for proper HTTP 301 SEO redirects. No client-side redirect needed here.
  },
  {
    path: "/account-types/:slug",
    name: "PublicAccountTypeDetail",
    component: () => import("../views/PublicAccountTypeDetail.vue"),
    props: true,
    meta: {
      requiresAuth: false,
    },
  },
  
  // Shared swipefile routes (public access)
  {
    path: "/shared/swipefiles/:token/board",
    name: "SharedSwipefileBoard",
    component: () => import("../views/PublicBoard.vue"),
    props: true,
    meta: {
      title: "Shared Board | VideoDatabase",
      description: "View a shared swipe board",
      requiresAuth: false,
    },
  },
  {
    path: "/public/swipefiles/:slug/board",
    name: "PublicSwipefileBoard",
    component: () => import("../views/PublicBoard.vue"),
    props: true,
    meta: {
      title: "Board | VideoDatabase",
      description: "View a public swipe board",
      requiresAuth: false,
    },
  },
  {
    path: "/shared/swipefiles/:token",
    name: "SharedSwipefileDetail",
    component: () => import("../views/SharedSwipefileDetail.vue"),
    props: true,
    meta: {
      title: "Shared Swipefile | VideoDatabase",
      description: "View shared video collection",
      requiresAuth: false,
    },
  },
  {
    path: "/public/swipefiles/:slug",
    name: "PublicSwipefileDetail",
    component: () => import("../views/PublicSwipefileDetail.vue"),
    props: true,
    meta: {
      title: "Swipefile | VideoDatabase",
      description: "View public video collection",
      requiresAuth: false,
    },
  },
  
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFound.vue"),
    meta: {
      title: "Page Not Found | VideoDatabase",
      description: "The requested page could not be found.",
      requiresAuth: false,
      skipInterestModal: true,
      noIndex: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Authentication and tier guard
router.beforeEach(async (to, from, next) => {
  if (typeof document !== 'undefined' && from.name && to.fullPath !== from.fullPath) {
    document.querySelectorAll('script[data-vdb-dynamic-schema]').forEach(element => element.remove());
  }
  // Only override title/description when the route explicitly provides them.
  // For detail pages (topics, formats, etc.), the middleware injects unique values
  // into the HTML and the component sets them after loading data. Overwriting with
  // a generic fallback here would flash duplicate titles to Google's JS renderer.
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  if (to.meta.description) {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", to.meta.description);
    }
  }
  
  // Check authentication requirements - be explicit about public routes
  const requiresAuth = to.meta.requiresAuth === true; // Only require auth if explicitly set to true
  const hasStoredToken = localStorage.getItem("access_token");
  const isActuallyAuthenticated = authManager.isAuthenticated || hasStoredToken;
  
  // Skip auth check entirely for public routes
  if (!requiresAuth) {
    return next();
  }
  
  // Wait for auth manager to finish loading if it's still loading
  if (authManager.loading) {
    await new Promise((resolve) => {
      const unsubscribe = authManager.addListener((authState) => {
        if (!authState.loading) {
          unsubscribe();
          resolve();
        }
      });
    });
  }
  
  // Additional check: if we have a stored token, assume we're authenticated
  // Check authentication for protected routes
  if (!isActuallyAuthenticated) {
    return next({
      name: 'Signup',
      query: { redirect: to.fullPath }
    });
  }
  
  if (to.name === 'Login' && isActuallyAuthenticated) {
    return next({ name: 'Home' });
  }

  if (to.name === 'Signup' && isActuallyAuthenticated) {
    return next({ name: 'Home' });
  }
  
  // Check tier requirements (only for authenticated users)
  if (isActuallyAuthenticated && to.meta.requiresTier) {
    try {
      // Get user tier
      const userProfile = await tierService.getUserTier(authManager.user.id);
      
      if (!userProfile) {
        console.error('User profile not found');
        return next({ name: 'Home' });
      }
      
      const requiredTiers = to.meta.requiresTier;
      const userTier = userProfile.tier;
      
      // Check if user has required tier
      if (!requiredTiers.includes(userTier)) {
        console.log(`Access denied: ${to.path} requires ${requiredTiers.join(' or ')} tier, user has ${userTier}`);
        return next({ name: 'Home' });
      }
    } catch (error) {
      console.error('Error checking user tier:', error);
      return next({ name: 'Home' });
    }
  }
  
  next();
});

// Google Analytics page view tracking
router.afterEach((to) => {
  const isEmbedRoute = to.meta?.embed === true;
  const isAdminRoute = to.path === '/admin' || to.path.startsWith('/admin/');

  if (typeof document !== 'undefined') {
    const isCut30Collection = /^\/views\/cut30sept2026\/?$/.test(to.path);
    if (isCut30Collection) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, follow';
    }
    const publicNoindexRoute = isCut30Collection || to.path === '/videos' || (to.path.startsWith('/video/') && !isIndexableVideoPilot(to.path)) || to.path === '/account-types/unknown' || to.name === 'BlogPreview';
    if (to.meta?.requiresAuth === false && !publicNoindexRoute && !isEmbedRoute) document.querySelector('meta[name="robots"]')?.remove();
  }

  if (!isEmbedRoute && !isAdminRoute && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-84B7V351W9', {
      page_path: to.path,
      page_title: to.meta.title || 'VideoDatabase | Instagram and TikTok Video Reference Library'
    });
  }

  const isEmbedClickthrough = String(to.query?.src || '').toLowerCase() === 'cut30_embed';
  if (isEmbedClickthrough && typeof window !== 'undefined') {
    const variantRaw = String(to.query?.variant || '').toLowerCase();
    const embedVariant = variantRaw === 'compact' ? 'compact' : 'standard';

    if (window.gtag) {
      window.gtag('event', 'embed_clickthrough_landing', {
        embed_source: 'cut30_embed',
        embed_variant: embedVariant,
        page_path: to.path,
      });
    }

    if (window.hmbl) {
      window.hmbl('track', 'embed_clickthrough_landing', {
        embed_source: 'cut30_embed',
        embed_variant: embedVariant,
        page_path: to.path,
      });
    }
  }

  if (!isEmbedRoute && !isAdminRoute && typeof window !== 'undefined') {
    window.requestAnimationFrame(labelUntrackedHumblyticsTargets);
  }
});

export default router;
